"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import { fetchWithAuth } from "@/lib/auth";
import { SELECTABLE_DESIGNATIONS, parseDesignations } from "@/lib/designations";
import { PresenterSyncPanel } from "./PresenterSyncPanel";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { TOURS } from "@/components/tours/catalog";
import { PhaseHelpTooltip } from "@/components/meetings/PhaseHelpTooltip";
import { GENDER_AI_HELP } from "@/components/directory/genderAiHelp";

interface GroupAssignment {
    entity_type: string;
    entity_id: number;
    entity_name: string;
    role: string;
}

interface VolunteerUser {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    profile_photo_url?: string;
    bio?: string;
    linkedin?: string;
    job_title?: string;
    company?: string;
    member_status?: string;
    is_presenter?: boolean;
    designations?: string;
    role?: string;
    program_journey?: string;
    city?: string;
    country?: string;
    gender_ai?: string;
    assignments: GroupAssignment[];
}

// CMT is locked/computed (synced from CMT Association records), so it is never a
// selectable chip here — use the shared selectable list.
const DESIGNATIONS = SELECTABLE_DESIGNATIONS;

const TYPE_BADGE: Record<string, string> = {
    committee: "bg-[#003d45] text-white",
    chapter:   "bg-[#007a8a] text-white",
    community: "bg-[#ae232d] text-white",
};

function Avatar({ user, size = "md" }: { user: VolunteerUser; size?: "sm" | "md" | "lg" }) {
    const sz = size === "lg" ? "w-16 h-16 text-xl" : size === "sm" ? "w-8 h-8 text-[10px]" : "w-10 h-10 text-sm";
    return user.profile_photo_url ? (
        <img src={user.profile_photo_url} alt={`${user.first_name} ${user.last_name}`}
            className={`${sz} rounded-full object-cover flex-shrink-0 border border-[#e2e8f0]`} />
    ) : (
        <div className={`${sz} rounded-full bg-[#003d45] text-white font-black flex items-center justify-center uppercase flex-shrink-0`}>
            {(user.first_name?.[0] || "")}{(user.last_name?.[0] || "")}
        </div>
    );
}

// ── Create Presenter Modal ──────────────────────────────────────────────────

interface CreatePresenterModalProps {
    onClose: () => void;
    onCreated: () => void;
}

function CreatePresenterModal({ onClose, onCreated }: CreatePresenterModalProps) {
    const [mode, setMode] = useState<"choose" | "existing" | "new">("choose");

    // All-users search for the picker
    const [existingSearch, setExistingSearch] = useState("");
    const [allUsers, setAllUsers] = useState<VolunteerUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState<VolunteerUser | null>(null);
    const [existingForm, setExistingForm] = useState({
        linkedin: "", job_title: "", company: "", designations: [] as string[],
        designation_other: "", bio: "",
    });
    const [existingPhoto, setExistingPhoto] = useState<File | null>(null);
    const [existingPhotoPreview, setExistingPhotoPreview] = useState<string>("");

    // New user flow
    const [newForm, setNewForm] = useState({
        first_name: "", last_name: "", email: "", mobile_no: "",
        job_title: "", company: "", linkedin: "",
        designations: [] as string[], designation_other: "", skills: "", bio: "",
    });
    const [newPhoto, setNewPhoto] = useState<File | null>(null);
    const [newPhotoPreview, setNewPhotoPreview] = useState<string>("");

    const [saving, setSaving] = useState(false);
    const [generatingBio, setGeneratingBio] = useState(false);
    const [error, setError] = useState("");

    const existingFileRef = useRef<HTMLInputElement>(null);
    const newFileRef = useRef<HTMLInputElement>(null);

    // Server-side search for the picker (DB has 40K+ users, client-side won't work)
    useEffect(() => {
        const trimmed = existingSearch.trim();
        if (!trimmed) { setAllUsers([]); return; }
        const timeout = setTimeout(() => {
            setLoadingUsers(true);
            fetchWithAuth(`/api/v1/organizations/users/all?q=${encodeURIComponent(trimmed)}&limit=50`)
                .then(r => r.ok ? r.json() : [])
                .then(d => setAllUsers(Array.isArray(d) ? d : []))
                .finally(() => setLoadingUsers(false));
        }, 300);
        return () => clearTimeout(timeout);
    }, [existingSearch]);

    const filteredUsers = allUsers;

    function handleExistingPhotoChange(file: File) {
        setExistingPhoto(file);
        setExistingPhotoPreview(URL.createObjectURL(file));
    }

    function handleNewPhotoChange(file: File) {
        setNewPhoto(file);
        setNewPhotoPreview(URL.createObjectURL(file));
    }

    function toggleDesignation(list: string[], val: string, setFn: (d: string[]) => void) {
        if (list.includes(val)) setFn(list.filter(d => d !== val));
        else setFn([...list, val]);
    }

    async function generateBio(name: string, jobTitle: string, company: string, linkedin: string,
        designations: string[], skills: string, bio: string, setBio: (b: string) => void) {
        if (!linkedin) { setError("Enter a LinkedIn URL first."); return; }
        setGeneratingBio(true); setError("");
        try {
            const res = await fetchWithAuth("/api/v1/presenters/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name, jobTitle, company, skills,
                    designation: designations.join(", "), linkedin, bio,
                }),
            });
            if (res.ok) { const d = await res.json(); setBio(d.bio || bio); }
            else { const e = await res.json().catch(() => ({})); setError(e.detail || "Bio generation failed."); }
        } finally { setGeneratingBio(false); }
    }

    async function submitExisting() {
        if (!selectedUser) return;
        setSaving(true); setError("");
        try {
            let photo_url = selectedUser.profile_photo_url || "";
            if (existingPhoto) {
                const form = new FormData();
                form.append("file", existingPhoto);
                const pr = await fetchWithAuth(`/api/v1/organizations/users/${selectedUser.id}/photo`, { method: "POST", body: form });
                if (pr.ok) { const d = await pr.json(); photo_url = d.profile_photo_url; }
            }
            const res = await fetchWithAuth("/api/v1/presenters", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: selectedUser.id,
                    linkedin: existingForm.linkedin,
                    job_title: existingForm.job_title,
                    company: existingForm.company,
                    designations: existingForm.designations.filter(d => d !== "Other").join(", "),
                    designation_other: existingForm.designations.includes("Other") ? existingForm.designation_other : "",
                    bio: existingForm.bio,
                    photo_url,
                }),
            });
            if (res.ok) { onCreated(); onClose(); }
            else { const e = await res.json().catch(() => ({})); setError(e.detail || "Failed to save presenter."); }
        } finally { setSaving(false); }
    }

    async function submitNew() {
        if (!newPhoto) { setError("A headshot photo is required."); return; }
        setSaving(true); setError("");
        try {
            const form = new FormData();
            form.append("first_name", newForm.first_name);
            form.append("last_name", newForm.last_name);
            form.append("email", newForm.email);
            if (newForm.mobile_no) form.append("mobile_no", newForm.mobile_no);
            if (newForm.job_title) form.append("job_title", newForm.job_title);
            if (newForm.company) form.append("company", newForm.company);
            form.append("linkedin", newForm.linkedin);
            const desigs = newForm.designations.filter(d => d !== "Other").join(", ");
            if (desigs) form.append("designations", desigs);
            if (newForm.designations.includes("Other") && newForm.designation_other)
                form.append("designation_other", newForm.designation_other);
            if (newForm.skills) form.append("skills", newForm.skills);
            if (newForm.bio) form.append("bio", newForm.bio);
            form.append("headshot", newPhoto);
            const res = await fetchWithAuth("/api/v1/presenters/external", { method: "POST", body: form });
            if (res.ok) { onCreated(); onClose(); }
            else { const e = await res.json().catch(() => ({})); setError(e.detail || "Failed to create presenter."); }
        } finally { setSaving(false); }
    }

    return (
        <div className="fixed inset-0 bg-[#003d45]/80 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl relative max-h-[92vh] flex flex-col">
                <div className="sticky top-0 bg-white border-b border-[#f0f0f0] px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
                    <div className="flex items-center gap-3">
                        {mode !== "choose" && (
                            <button onClick={() => { setMode("choose"); setSelectedUser(null); setExistingSearch(""); setError(""); }}
                                className="text-[#999] hover:text-[#333]">
                                <span className="material-symbols-outlined">arrow_back</span>
                            </button>
                        )}
                        <h3 className="text-xl font-black text-[#003d45]">
                            {mode === "choose" ? "Create Volunteer" : mode === "existing" ? "Complete Profile" : "Add New Volunteer"}
                        </h3>
                    </div>
                    <button onClick={onClose} className="text-[#999] hover:text-[#333]">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
                    )}

                    {/* Choose mode — search all users, pick one to make presenter */}
                    {mode === "choose" && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-[#666]">Search for a user and add their details to make them a presenter.</p>
                                <button onClick={() => setMode("new")}
                                    className="flex items-center gap-1 text-xs font-bold text-[#007a8a] hover:text-[#003d45] transition-colors flex-shrink-0 ml-3">
                                    <span className="material-symbols-outlined text-[14px]">person_add</span>
                                    New Person
                                </button>
                            </div>
                            <div className="relative">
                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#999] text-base">search</span>
                                <input type="text" value={existingSearch} onChange={e => setExistingSearch(e.target.value)}
                                    placeholder="Search by name, email, or company…"
                                    autoFocus
                                    className="w-full pl-9 pr-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                            </div>
                            <div className="space-y-1 max-h-[50vh] overflow-y-auto pr-1">
                                {loadingUsers ? (
                                    <div className="flex justify-center py-8">
                                        <div className="w-6 h-6 border-2 border-[#e2e8f0] border-t-[#003d45] rounded-full animate-spin" />
                                    </div>
                                ) : !existingSearch.trim() ? (
                                    <p className="text-sm text-[#bbb] italic px-3 py-6 text-center">Type to search…</p>
                                ) : filteredUsers.length === 0 ? (
                                    <p className="text-sm text-[#bbb] italic px-3 py-6 text-center">No users found.</p>
                                ) : filteredUsers.map(u => (
                                    <button key={u.id} onClick={() => {
                                        setSelectedUser(u);
                                        setExistingForm(f => ({
                                            ...f,
                                            linkedin: u.linkedin || "",
                                            job_title: u.job_title || "",
                                            company: u.company || "",
                                            bio: u.bio || "",
                                        }));
                                        setMode("existing");
                                    }}
                                        className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#f4f8f8] text-left border border-transparent hover:border-[#e2e8f0] transition-all group">
                                        <Avatar user={u} size="sm" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <p className="text-sm font-bold text-[#003d45]">{u.first_name} {u.last_name}</p>
                                                {u.is_presenter && <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#007a8a] text-white">Already Presenter</span>}
                                            </div>
                                            <p className="text-xs text-[#888] truncate">{u.email}{u.company ? ` · ${u.company}` : ""}</p>
                                        </div>
                                        <div className="flex gap-1 flex-shrink-0">
                                            {!u.linkedin && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">No LinkedIn</span>}
                                            {!u.profile_photo_url && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">No Photo</span>}
                                        </div>
                                        <span className="material-symbols-outlined text-[#ccc] group-hover:text-[#007a8a] text-base transition-colors">chevron_right</span>
                                    </button>
                                ))}
                                {existingSearch && filteredUsers.length === 50 && (
                                    <p className="text-center text-[10px] text-[#bbb] pt-2">Showing top 50 results — refine your search to narrow down</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Existing user flow — always has selectedUser set from the list */}
                    {mode === "existing" && selectedUser && (
                        <div className="space-y-5">
                            <>
                                    <div className="flex items-center gap-3 p-3 bg-[#f4f8f8] rounded-xl border border-[#e2e8f0]">
                                        <Avatar user={selectedUser} size="sm" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-black text-[#003d45]">{selectedUser.first_name} {selectedUser.last_name}</p>
                                            <p className="text-xs text-[#888]">{selectedUser.email}</p>
                                        </div>
                                        <div className="flex gap-1">
                                            {!selectedUser.linkedin && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">No LinkedIn</span>}
                                            {!selectedUser.profile_photo_url && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">No Photo</span>}
                                        </div>
                                    </div>

                                    {/* Photo upload */}
                                    <div>
                                        <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-2">Profile Photo</label>
                                        <div className="flex items-center gap-4">
                                            {existingPhotoPreview ? (
                                                <img src={existingPhotoPreview} className="w-16 h-16 rounded-full object-cover border border-[#e2e8f0]" />
                                            ) : selectedUser.profile_photo_url ? (
                                                <img src={selectedUser.profile_photo_url} className="w-16 h-16 rounded-full object-cover border border-[#e2e8f0]" />
                                            ) : (
                                                <div className="w-16 h-16 rounded-full bg-[#003d45] text-white font-black flex items-center justify-center uppercase text-xl">
                                                    {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                                                </div>
                                            )}
                                            <button onClick={() => existingFileRef.current?.click()}
                                                className="px-4 py-2 border border-[#e2e8f0] rounded-xl text-sm font-bold text-[#007a8a] hover:bg-[#007a8a]/5 transition-colors">
                                                {existingPhotoPreview || selectedUser.profile_photo_url ? "Change Photo" : "Upload Photo"}
                                            </button>
                                            <input ref={existingFileRef} type="file" accept="image/*" className="hidden"
                                                onChange={e => { const f = e.target.files?.[0]; if (f) handleExistingPhotoChange(f); }} />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">LinkedIn URL <span className="text-red-500">*</span></label>
                                        <input type="url" value={existingForm.linkedin} onChange={e => setExistingForm(f => ({ ...f, linkedin: e.target.value }))}
                                            placeholder="https://linkedin.com/in/username"
                                            className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Job Title</label>
                                            <input type="text" value={existingForm.job_title} onChange={e => setExistingForm(f => ({ ...f, job_title: e.target.value }))}
                                                placeholder="e.g. Portfolio Manager"
                                                className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Company</label>
                                            <input type="text" value={existingForm.company} onChange={e => setExistingForm(f => ({ ...f, company: e.target.value }))}
                                                placeholder="e.g. Acme Capital"
                                                className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-2">Designations</label>
                                        {(() => {
                                            const locked = parseDesignations(selectedUser.designations || "").lockedPresent;
                                            if (locked.length === 0) return null;
                                            return (
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {locked.map(d => (
                                                        <span key={d} title="Synced from CMT Association records — not editable"
                                                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-[#1a1a1a] text-white border border-[#1a1a1a] cursor-default select-none">
                                                            <span className="material-symbols-outlined text-[13px]">lock</span>{d}
                                                        </span>
                                                    ))}
                                                </div>
                                            );
                                        })()}
                                        <div className="flex flex-wrap gap-2">
                                            {DESIGNATIONS.map(d => (
                                                <button key={d} type="button"
                                                    onClick={() => toggleDesignation(existingForm.designations, d, des => setExistingForm(f => ({ ...f, designations: des })))}
                                                    className={`px-3 py-1 rounded-full text-xs font-bold border transition-all ${existingForm.designations.includes(d) ? "bg-[#007a8a] text-white border-[#007a8a]" : "border-[#e2e8f0] text-[#666] hover:border-[#007a8a]"}`}>
                                                    {d}
                                                </button>
                                            ))}
                                        </div>
                                        {existingForm.designations.includes("Other") && (
                                            <input type="text" value={existingForm.designation_other} onChange={e => setExistingForm(f => ({ ...f, designation_other: e.target.value }))}
                                                placeholder="Specify other designation"
                                                className="mt-2 w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                        )}
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="block text-xs font-bold text-[#666] uppercase tracking-wider">Bio</label>
                                            <button type="button" disabled={generatingBio} onClick={() =>
                                                generateBio(
                                                    `${selectedUser.first_name} ${selectedUser.last_name}`,
                                                    existingForm.job_title, existingForm.company, existingForm.linkedin,
                                                    [...parseDesignations(selectedUser.designations || "").lockedPresent, ...existingForm.designations], "", existingForm.bio,
                                                    bio => setExistingForm(f => ({ ...f, bio }))
                                                )}
                                                className="flex items-center gap-1 text-xs font-bold text-[#007a8a] hover:text-[#003d45] transition-colors disabled:opacity-50">
                                                {generatingBio
                                                    ? <div className="w-3 h-3 border border-[#007a8a]/30 border-t-[#007a8a] rounded-full animate-spin" />
                                                    : <span className="material-symbols-outlined text-sm">auto_awesome</span>}
                                                {generatingBio ? "Generating…" : "Generate Bio"}
                                            </button>
                                        </div>
                                        <textarea value={existingForm.bio} onChange={e => setExistingForm(f => ({ ...f, bio: e.target.value }))}
                                            rows={5} placeholder="Professional biography…"
                                            className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a] resize-none" />
                                    </div>

                                    <div className="flex gap-3 pt-2">
                                        <button onClick={onClose}
                                            className="flex-1 py-3 rounded-xl border border-[#e2e8f0] text-sm font-bold text-[#666] hover:bg-[#f9f9f9] transition-colors">
                                            Cancel
                                        </button>
                                        <button onClick={submitExisting} disabled={saving}
                                            className="flex-1 py-3 rounded-xl bg-[#003d45] hover:bg-[#007a8a] text-white font-black uppercase tracking-wider text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                            {saving ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : "Save Volunteer"}
                                        </button>
                                    </div>
                                </>
                        </div>
                    )}

                    {/* New user flow */}
                    {mode === "new" && (
                        <div className="space-y-5">
                            {/* Photo */}
                            <div>
                                <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-2">Headshot Photo <span className="text-red-500">*</span></label>
                                <div className="flex items-center gap-4">
                                    {newPhotoPreview ? (
                                        <img src={newPhotoPreview} className="w-16 h-16 rounded-full object-cover border border-[#e2e8f0]" />
                                    ) : (
                                        <div className="w-16 h-16 rounded-full bg-[#e2e8f0] flex items-center justify-center">
                                            <span className="material-symbols-outlined text-[#999]">person</span>
                                        </div>
                                    )}
                                    <button onClick={() => newFileRef.current?.click()}
                                        className="px-4 py-2 border border-[#e2e8f0] rounded-xl text-sm font-bold text-[#007a8a] hover:bg-[#007a8a]/5 transition-colors">
                                        {newPhotoPreview ? "Change Photo" : "Upload Photo"}
                                    </button>
                                    <input ref={newFileRef} type="file" accept="image/*" className="hidden"
                                        onChange={e => { const f = e.target.files?.[0]; if (f) handleNewPhotoChange(f); }} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">First Name <span className="text-red-500">*</span></label>
                                    <input type="text" value={newForm.first_name} onChange={e => setNewForm(f => ({ ...f, first_name: e.target.value }))}
                                        placeholder="First name"
                                        className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Last Name <span className="text-red-500">*</span></label>
                                    <input type="text" value={newForm.last_name} onChange={e => setNewForm(f => ({ ...f, last_name: e.target.value }))}
                                        placeholder="Last name"
                                        className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Email <span className="text-red-500">*</span></label>
                                    <input type="email" value={newForm.email} onChange={e => setNewForm(f => ({ ...f, email: e.target.value }))}
                                        placeholder="email@example.com"
                                        className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Mobile</label>
                                    <input type="tel" value={newForm.mobile_no} onChange={e => setNewForm(f => ({ ...f, mobile_no: e.target.value }))}
                                        placeholder="+1 555 000 0000"
                                        className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Job Title</label>
                                    <input type="text" value={newForm.job_title} onChange={e => setNewForm(f => ({ ...f, job_title: e.target.value }))}
                                        placeholder="e.g. Portfolio Manager"
                                        className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Company</label>
                                    <input type="text" value={newForm.company} onChange={e => setNewForm(f => ({ ...f, company: e.target.value }))}
                                        placeholder="e.g. Acme Capital"
                                        className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">LinkedIn URL <span className="text-red-500">*</span></label>
                                <input type="url" value={newForm.linkedin} onChange={e => setNewForm(f => ({ ...f, linkedin: e.target.value }))}
                                    placeholder="https://linkedin.com/in/username"
                                    className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Skills / Expertise</label>
                                <input type="text" value={newForm.skills} onChange={e => setNewForm(f => ({ ...f, skills: e.target.value }))}
                                    placeholder="e.g. Technical Analysis, Equity Research"
                                    className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-2">Designations</label>
                                <div className="flex flex-wrap gap-2">
                                    {DESIGNATIONS.map(d => (
                                        <button key={d} type="button"
                                            onClick={() => toggleDesignation(newForm.designations, d, des => setNewForm(f => ({ ...f, designations: des })))}
                                            className={`px-3 py-1 rounded-full text-xs font-bold border transition-all ${newForm.designations.includes(d) ? "bg-[#007a8a] text-white border-[#007a8a]" : "border-[#e2e8f0] text-[#666] hover:border-[#007a8a]"}`}>
                                            {d}
                                        </button>
                                    ))}
                                </div>
                                {newForm.designations.includes("Other") && (
                                    <input type="text" value={newForm.designation_other} onChange={e => setNewForm(f => ({ ...f, designation_other: e.target.value }))}
                                        placeholder="Specify other designation"
                                        className="mt-2 w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                )}
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-xs font-bold text-[#666] uppercase tracking-wider">Bio <span className="text-[#999] font-normal normal-case">(auto-generated if blank)</span></label>
                                    <button type="button" disabled={generatingBio} onClick={() =>
                                        generateBio(
                                            `${newForm.first_name} ${newForm.last_name}`,
                                            newForm.job_title, newForm.company, newForm.linkedin,
                                            newForm.designations, newForm.skills, newForm.bio,
                                            bio => setNewForm(f => ({ ...f, bio }))
                                        )}
                                        className="flex items-center gap-1 text-xs font-bold text-[#007a8a] hover:text-[#003d45] transition-colors disabled:opacity-50">
                                        {generatingBio
                                            ? <div className="w-3 h-3 border border-[#007a8a]/30 border-t-[#007a8a] rounded-full animate-spin" />
                                            : <span className="material-symbols-outlined text-sm">auto_awesome</span>}
                                        {generatingBio ? "Generating…" : "Generate Bio"}
                                    </button>
                                </div>
                                <textarea value={newForm.bio} onChange={e => setNewForm(f => ({ ...f, bio: e.target.value }))}
                                    rows={5} placeholder="Professional biography… (leave blank to auto-generate)"
                                    className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a] resize-none" />
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button onClick={onClose}
                                    className="flex-1 py-3 rounded-xl border border-[#e2e8f0] text-sm font-bold text-[#666] hover:bg-[#f9f9f9] transition-colors">
                                    Cancel
                                </button>
                                <button onClick={submitNew} disabled={saving}
                                    className="flex-1 py-3 rounded-xl bg-[#003d45] hover:bg-[#007a8a] text-white font-black uppercase tracking-wider text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                    {saving ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : "Create Volunteer"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Edit Profile Modal ──────────────────────────────────────────────────────

interface EditModalProps {
    user: VolunteerUser;
    onClose: () => void;
    onSaved: (updated: VolunteerUser) => void;
}

function EditModal({ user, onClose, onSaved }: EditModalProps) {
    const [form, setForm] = useState({ bio: user.bio || "", linkedin: user.linkedin || "", job_title: user.job_title || "", company: user.company || "" });
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [generatingBio, setGeneratingBio] = useState(false);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [localUser, setLocalUser] = useState(user);
    const fileInputRef = useRef<HTMLInputElement>(null);

    async function handleSave() {
        setSaving(true);
        const res = await fetchWithAuth(`/api/v1/organizations/users/${localUser.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(form),
        });
        if (res.ok) {
            const updated = await res.json();
            onSaved({ ...localUser, ...updated });
            setSaveSuccess(true);
            setTimeout(() => { setSaveSuccess(false); onClose(); }, 1500);
        } else { alert("Failed to save changes."); }
        setSaving(false);
    }

    async function handleGenerateBio() {
        const linkedin = form.linkedin.trim();
        if (!linkedin) { alert("Enter a LinkedIn URL first."); return; }
        setGeneratingBio(true);
        try {
            const res = await fetchWithAuth("/api/v1/presenters/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: `${localUser.first_name} ${localUser.last_name}`,
                    jobTitle: form.job_title || localUser.job_title || "",
                    company: form.company || localUser.company || "",
                    skills: "", designation: "", linkedin, bio: form.bio || "",
                }),
            });
            if (res.ok) { const d = await res.json(); setForm(p => ({ ...p, bio: d.bio || p.bio })); }
            else { const e = await res.json().catch(() => ({})); alert(e.detail || "Bio generation failed."); }
        } finally { setGeneratingBio(false); }
    }

    async function handlePhotoUpload(file: File) {
        if (file.size > 35 * 1024 * 1024) { alert("Photo must be under 35 MB."); return; }
        setPhotoUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        try {
            const res = await fetchWithAuth(`/api/v1/organizations/users/${localUser.id}/photo`, { method: "POST", body: formData });
            if (res.ok) { const d = await res.json(); setLocalUser(u => ({ ...u, profile_photo_url: d.profile_photo_url })); }
            else { alert("Photo upload failed."); }
        } finally { setPhotoUploading(false); }
    }

    return (
        <div className="fixed inset-0 bg-[#003d45]/80 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl relative max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-[#f0f0f0] px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
                    <h3 className="text-xl font-black text-[#003d45]">Edit Profile</h3>
                    <button onClick={onClose} className="text-[#999] hover:text-[#333]">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
                <div className="p-6 space-y-5">
                    {saveSuccess && (
                        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700 font-semibold">
                            <span className="material-symbols-outlined text-[16px]">check_circle</span>
                            Changes saved successfully!
                        </div>
                    )}
                    <div className="flex items-center gap-4">
                        <div className="relative flex-shrink-0">
                            <Avatar user={localUser} size="lg" />
                            <button onClick={() => fileInputRef.current?.click()} disabled={photoUploading}
                                className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#007a8a] text-white rounded-full flex items-center justify-center hover:bg-[#003d45] transition-colors">
                                {photoUploading
                                    ? <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                                    : <span className="material-symbols-outlined text-[12px]">photo_camera</span>}
                            </button>
                            <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                                onChange={e => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(f); }} />
                        </div>
                        <div>
                            <p className="font-black text-lg text-[#003d45]">{localUser.first_name} {localUser.last_name}</p>
                            <p className="text-sm text-[#888]">{localUser.email}</p>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Job Title</label>
                        <input type="text" value={form.job_title} onChange={e => setForm(p => ({ ...p, job_title: e.target.value }))}
                            placeholder="e.g. Portfolio Manager"
                            className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Company</label>
                        <input type="text" value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                            placeholder="e.g. Acme Capital"
                            className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">LinkedIn URL</label>
                        <input type="url" value={form.linkedin} onChange={e => setForm(p => ({ ...p, linkedin: e.target.value }))}
                            placeholder="https://linkedin.com/in/username"
                            className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-[#666] uppercase tracking-wider">Bio</label>
                            <button onClick={handleGenerateBio} disabled={generatingBio}
                                className="flex items-center gap-1 text-xs font-bold text-[#007a8a] hover:text-[#003d45] transition-colors disabled:opacity-50">
                                {generatingBio ? <div className="w-3 h-3 border border-[#007a8a]/30 border-t-[#007a8a] rounded-full animate-spin" /> : <span className="material-symbols-outlined text-sm">auto_awesome</span>}
                                {generatingBio ? "Generating…" : "Regenerate Bio"}
                            </button>
                        </div>
                        <textarea value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                            rows={6} placeholder="Professional biography…"
                            className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a] resize-none" />
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button onClick={onClose}
                            className="flex-1 py-3 rounded-xl border border-[#e2e8f0] text-sm font-bold text-[#666] hover:bg-[#f9f9f9] transition-colors">
                            Cancel
                        </button>
                        <button onClick={handleSave} disabled={saving}
                            className="flex-1 py-3 rounded-xl bg-[#003d45] hover:bg-[#007a8a] text-white font-black uppercase tracking-wider text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                            {saving ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : "Save Changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Multi-Select Dropdown ─────────────────────────────────────────────────────

function MultiSelectDropdown({
    label, options, selected, onToggle, onClear,
}: {
    label: string;
    options: string[];
    selected: Set<string>;
    onToggle: (val: string) => void;
    onClear: () => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handler(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const count = selected.size;
    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(p => !p)}
                className={`flex items-center gap-2 px-3 py-2.5 border rounded-xl text-sm bg-white focus:outline-none transition-colors whitespace-nowrap ${count > 0 ? "border-[#003d45] text-[#003d45] font-bold" : "border-[#e2e8f0] text-[#555] hover:border-[#bbb]"}`}
            >
                {label}
                {count > 0 && (
                    <span className="bg-[#003d45] text-white text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none">{count}</span>
                )}
                <span className="material-symbols-outlined text-[16px] text-[#aaa]">{open ? "expand_less" : "expand_more"}</span>
            </button>

            {open && (
                <div className="absolute top-full left-0 mt-1.5 bg-white border border-[#e2e8f0] rounded-xl shadow-xl z-30 min-w-[200px] py-1.5 overflow-hidden">
                    {count > 0 && (
                        <button
                            onClick={() => { onClear(); setOpen(false); }}
                            className="w-full text-left px-3 py-2 text-[11px] text-[#ae232d] font-bold hover:bg-red-50 transition-colors border-b border-[#f0f0f0] flex items-center gap-1.5"
                        >
                            <span className="material-symbols-outlined text-[13px]">close</span>
                            Clear selection
                        </button>
                    )}
                    <div className="max-h-64 overflow-y-auto">
                        {options.map(opt => (
                            <label key={opt} className="flex items-center gap-2.5 px-3 py-2 hover:bg-[#f4f8f8] cursor-pointer transition-colors">
                                <input
                                    type="checkbox"
                                    checked={selected.has(opt)}
                                    onChange={() => onToggle(opt)}
                                    className="accent-[#003d45] w-3.5 h-3.5 flex-shrink-0"
                                />
                                <span className="text-sm text-[#333] truncate">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
    return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#003d45]/10 text-[#003d45] text-[11px] font-bold rounded-full border border-[#003d45]/20">
            {label}
            <button onClick={onRemove} className="hover:text-[#ae232d] transition-colors ml-0.5">
                <span className="material-symbols-outlined text-[12px]">close</span>
            </button>
        </span>
    );
}

// ── Main Panel ──────────────────────────────────────────────────────────────

export function VolunteersPanel() {
    const [users, setUsers] = useState<VolunteerUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filterPresenterType, setFilterPresenterType] = useState<"all" | "presenter" | "volunteer">("all");
    const [filterGroups, setFilterGroups] = useState<Set<string>>(new Set());
    const [filterRoles, setFilterRoles] = useState<Set<string>>(new Set());

    const [editUser, setEditUser] = useState<VolunteerUser | null>(null);
    const [showCreatePresenter, setShowCreatePresenter] = useState(false);
    const [showPresenterSync, setShowPresenterSync] = useState(false);
    const { replay: replayTour } = useAutoTour("staff.volunteers", TOURS["staff.volunteers"]);

    useEffect(() => { loadUsers(); }, []);

    function loadUsers() {
        setLoading(true);
        fetchWithAuth("/api/v1/organizations/users?limit=1000")
            .then(r => r.ok ? r.json() : [])
            .then(d => setUsers(Array.isArray(d) ? d : []))
            .finally(() => setLoading(false));
    }

    function toggleFilter(set: Set<string>, val: string): Set<string> {
        const next = new Set(set);
        if (next.has(val)) next.delete(val); else next.add(val);
        return next;
    }

    const allGroupNames = useMemo(() => {
        const s = new Set<string>();
        users.forEach(u => u.assignments?.forEach(a => s.add(a.entity_name)));
        return Array.from(s).sort();
    }, [users]);

    const allAssignmentRoles = useMemo(() => {
        const s = new Set<string>();
        users.forEach(u => u.assignments?.forEach(a => s.add(a.role)));
        return Array.from(s).sort();
    }, [users]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return users.filter(u => {
            if (filterPresenterType === "presenter" && !u.is_presenter) return false;
            if (filterPresenterType === "volunteer" && u.is_presenter) return false;
            if (filterGroups.size > 0 && !u.assignments?.some(a => filterGroups.has(a.entity_name))) return false;
            if (filterRoles.size > 0 && !u.assignments?.some(a => filterRoles.has(a.role))) return false;
            if (q) {
                const hay = [u.first_name, u.last_name, u.email, u.company, u.job_title,
                    ...(u.assignments?.map(a => a.entity_name) ?? [])].filter(Boolean).join(" ").toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [users, search, filterPresenterType, filterGroups, filterRoles]);

    const presenterCount = users.filter(u => u.is_presenter).length;
    const withAssignments = users.filter(u => u.assignments?.length > 0).length;

    const hasActiveFilters = search || filterPresenterType !== "all" || filterGroups.size > 0 || filterRoles.size > 0;

    function clearAllFilters() {
        setSearch(""); setFilterPresenterType("all"); setFilterGroups(new Set()); setFilterRoles(new Set());
    }

    if (showPresenterSync) {
        return <PresenterSyncPanel onBack={() => setShowPresenterSync(false)} />;
    }

    return (
        <div className="space-y-6">
            {/* Header row */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-black text-[#003d45] mb-1">Volunteers</h2>
                    <p className="text-sm text-[#666]">
                        {filtered.length} of {users.length} people shown ·{" "}
                        <span className="font-semibold">{presenterCount} presenters</span> ·{" "}
                        <span className="font-semibold">{withAssignments} with group assignments</span>
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <TourButton onClick={replayTour} />
                    <button onClick={() => setShowPresenterSync(true)}
                        className="flex items-center gap-2 border border-[#e2e8f0] bg-white hover:border-[#003d45] hover:text-[#003d45] text-[#555] px-4 py-2.5 rounded-xl text-sm font-bold transition-colors">
                        <span className="material-symbols-outlined text-[18px]">sync_alt</span>
                        Presenter Sync
                    </button>
                    <button onClick={() => setShowCreatePresenter(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-[#003d45] hover:bg-[#007a8a] text-white font-black text-sm rounded-xl transition-colors">
                        <span className="material-symbols-outlined text-[18px]">person_add</span>
                        + Create Volunteer
                    </button>
                </div>
            </div>

            {/* Summary pills */}
            <div id="vols-stats" className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { label: "Total People",     value: users.length,          icon: "group",          cls: "bg-[#003d45]/10 text-[#003d45]" },
                    { label: "Presenters",        value: presenterCount,        icon: "mic",            cls: "bg-[#007a8a]/10 text-[#007a8a]" },
                    { label: "With Assignments",  value: withAssignments,       icon: "assignment_ind", cls: "bg-emerald-100 text-emerald-700" },
                    { label: "With Bio",          value: users.filter(u => u.bio?.trim()).length, icon: "description", cls: "bg-amber-100 text-amber-700" },
                ].map(s => (
                    <div key={s.label} className="bg-white rounded-2xl border border-[#e2e8f0] p-4 flex items-center gap-3 shadow-sm">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${s.cls}`}>
                            <span className="material-symbols-outlined text-lg">{s.icon}</span>
                        </div>
                        <div>
                            <p className="text-2xl font-black text-[#003d45]">{s.value}</p>
                            <p className="text-[10px] font-bold uppercase tracking-wide text-[#999]">{s.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div id="vols-filters" className="space-y-3">
                <div className="flex flex-wrap gap-2.5 items-center">
                    <div className="relative flex-1 min-w-[200px] max-w-sm">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#999] text-base">search</span>
                        <input type="text" placeholder="Search name, email, group…" value={search} onChange={e => setSearch(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a] bg-white" />
                    </div>

                    {/* Presenter type — exclusive toggle pills */}
                    <div className="flex items-center bg-white border border-[#e2e8f0] rounded-xl overflow-hidden text-xs font-bold">
                        {([["all", "All"], ["presenter", "Presenters"], ["volunteer", "Volunteers"]] as const).map(([val, label]) => (
                            <button key={val} onClick={() => setFilterPresenterType(val)}
                                className={`px-3 py-2.5 transition-colors ${filterPresenterType === val ? "bg-[#003d45] text-white" : "text-[#666] hover:bg-[#f0f0f0]"}`}>
                                {label}
                            </button>
                        ))}
                    </div>

                    <MultiSelectDropdown
                        label="Groups"
                        options={allGroupNames}
                        selected={filterGroups}
                        onToggle={val => setFilterGroups(toggleFilter(filterGroups, val))}
                        onClear={() => setFilterGroups(new Set())}
                    />

                    <MultiSelectDropdown
                        label="Assignment Role"
                        options={allAssignmentRoles}
                        selected={filterRoles}
                        onToggle={val => setFilterRoles(toggleFilter(filterRoles, val))}
                        onClear={() => setFilterRoles(new Set())}
                    />

                    {hasActiveFilters && (
                        <button onClick={clearAllFilters} className="text-xs text-[#ae232d] font-bold hover:underline flex items-center gap-1">
                            <span className="material-symbols-outlined text-[13px]">close</span>
                            Clear all
                        </button>
                    )}
                </div>

                {/* Active filter chips */}
                {hasActiveFilters && (
                    <div className="flex flex-wrap gap-2 items-center">
                        {search && <FilterChip label={`"${search}"`} onRemove={() => setSearch("")} />}
                        {filterPresenterType !== "all" && (
                            <FilterChip label={filterPresenterType === "presenter" ? "Presenters" : "Volunteers"} onRemove={() => setFilterPresenterType("all")} />
                        )}
                        {Array.from(filterGroups).map(g => (
                            <FilterChip key={g} label={g} onRemove={() => setFilterGroups(toggleFilter(filterGroups, g))} />
                        ))}
                        {Array.from(filterRoles).map(r => (
                            <FilterChip key={r} label={r} onRemove={() => setFilterRoles(toggleFilter(filterRoles, r))} />
                        ))}
                    </div>
                )}
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex items-center justify-center h-40">
                    <div className="w-7 h-7 border-2 border-[#e2e8f0] border-t-[#003d45] rounded-full animate-spin" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-16 text-[#999] text-sm">No people match your filters.</div>
            ) : (
                <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
                    {/* Table header — desktop only */}
                    <div className="hidden lg:grid grid-cols-[2.5fr_0.8fr_2fr_1.5fr_1.2fr_auto] gap-4 px-5 py-3 border-b border-[#e2e8f0] bg-[#f8fafa]">
                        {["VOLUNTEER PROFILE", "STATUS", "GROUP ASSIGNMENTS", "BIO", "NEXT ACTIONS", ""].map((h, i) => (
                            <div key={i} className="text-[10px] font-black uppercase tracking-widest text-[#999]">{h}</div>
                        ))}
                    </div>

                    {/* Table rows */}
                    <div className="divide-y divide-[#f0f4f4]">
                        {filtered.map(u => {
                            const memberStatusActive = u.member_status?.toLowerCase() === "active";
                            const rawBio = u.bio?.trim().replace(/<[^>]+>/g, "") || "";
                            const bioSnippet = rawBio ? rawBio.slice(0, 120) + (rawBio.length > 120 ? "…" : "") : null;

                            return (
                                <div key={u.id}
                                    className="flex flex-col gap-3 lg:grid lg:grid-cols-[2.5fr_0.8fr_2fr_1.5fr_1.2fr_auto] lg:gap-4 px-5 py-4 items-start hover:bg-[#f8fafa] transition-colors">

                                    {/* Volunteer Profile */}
                                    <div className="flex items-start gap-3 min-w-0">
                                        <Avatar user={u} size="md" />
                                        <div className="min-w-0 space-y-0.5">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <p className="font-black text-sm text-[#003d45]">{u.first_name} {u.last_name}</p>
                                                <span className={`text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded-full flex-shrink-0 ${u.is_presenter ? "bg-[#007a8a]/10 text-[#007a8a]" : "bg-slate-100 text-slate-500"}`}>
                                                    {u.is_presenter ? "Presenter" : "Volunteer"}
                                                </span>
                                            </div>
                                            {(u.job_title || u.company) && (
                                                <p className="text-[11px] text-[#777] truncate">{[u.job_title, u.company].filter(Boolean).join(" · ")}</p>
                                            )}
                                            <p className="text-[11px] text-[#aaa] flex items-center gap-0.5">
                                                <span className="material-symbols-outlined text-[11px]">location_on</span>
                                                {(u.city || u.country) ? [u.city, u.country].filter(Boolean).join(", ") : <span className="italic">No location</span>}
                                            </p>
                                            {u.gender_ai && (
                                                <p className="text-[10px] text-[#aaa] flex items-center gap-0.5">
                                                    <span className="material-symbols-outlined text-[11px]">person</span>
                                                    {u.gender_ai}
                                                    {/* This value was shown bare here, with no hint that it is a guess. */}
                                                    <PhaseHelpTooltip
                                                        title={GENDER_AI_HELP.title}
                                                        body={GENDER_AI_HELP.body}
                                                        items={GENDER_AI_HELP.items}
                                                        align="left"
                                                    >
                                                        <span
                                                            className="material-symbols-outlined text-[11px] text-amber-600/80 cursor-help"
                                                            aria-label="How the gender value is produced"
                                                        >info</span>
                                                    </PhaseHelpTooltip>
                                                </p>
                                            )}
                                            {/* Contact pills */}
                                            <div className="flex flex-wrap gap-1 pt-0.5">
                                                <a href={`mailto:${u.email}`}
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-[9px] text-[#666] font-medium transition-colors"
                                                    title={u.email}>
                                                    <span className="material-symbols-outlined text-[9px]">mail</span>
                                                    Email
                                                </a>
                                                {u.linkedin ? (
                                                    <a href={u.linkedin} target="_blank" rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#0a66c2]/10 hover:bg-[#0a66c2]/20 text-[9px] text-[#0a66c2] font-medium transition-colors">
                                                        <span className="material-symbols-outlined text-[9px]">open_in_new</span>
                                                        LinkedIn
                                                    </a>
                                                ) : (
                                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 text-[9px] text-amber-600 font-medium">
                                                        No LinkedIn
                                                    </span>
                                                )}
                                                {u.is_presenter && (
                                                    <a href={`/volunteers/${u.id}`} target="_blank" rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#007a8a]/10 hover:bg-[#007a8a]/20 text-[9px] text-[#007a8a] font-black uppercase tracking-wider transition-colors">
                                                        <span className="material-symbols-outlined text-[9px]">open_in_new</span>
                                                        View Page
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status dot */}
                                    <div className="flex items-center gap-1.5 pt-0.5">
                                        <span className="lg:hidden text-[9px] font-black uppercase tracking-widest text-[#bbb] mr-1">Status</span>
                                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${memberStatusActive ? "bg-emerald-500" : "bg-red-400"}`} />
                                        <span className="text-[11px] text-[#888] capitalize">{u.member_status || "—"}</span>
                                    </div>

                                    {/* Group assignments — colored pills stacked */}
                                    <div className="min-w-0 pt-0.5 flex flex-col gap-1">
                                        <span className="lg:hidden text-[9px] font-black uppercase tracking-widest text-[#bbb]">Group Assignments</span>
                                        {u.assignments?.length > 0 ? (
                                            u.assignments.map((a, i) => (
                                                <span key={i} className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full self-start ${TYPE_BADGE[a.entity_type] || "bg-slate-200 text-slate-600"}`}>
                                                    {a.entity_name}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-[#ccc] text-sm">—</span>
                                        )}
                                    </div>

                                    {/* Bio */}
                                    <div className="text-[11px] text-[#666] leading-relaxed pt-0.5">
                                        <span className="lg:hidden block text-[9px] font-black uppercase tracking-widest text-[#bbb] mb-0.5">Bio</span>
                                        {bioSnippet
                                            ? <span className="text-[#555]">{bioSnippet}</span>
                                            : <span className="text-[#ccc] italic">No bio</span>}
                                    </div>

                                    {/* Next Actions */}
                                    <div className="text-[11px] text-[#888] pt-0.5">
                                        <span className="lg:hidden block text-[9px] font-black uppercase tracking-widest text-[#bbb] mb-0.5">Next Actions</span>
                                        {!u.linkedin && (
                                            <div>
                                                <span className="text-[#ae232d] font-bold text-[10px]">Action needed</span>
                                                <p className="text-[10px] text-[#999]">Add LinkedIn</p>
                                            </div>
                                        )}
                                        {!u.bio?.trim() && u.linkedin && (
                                            <div>
                                                <span className="text-[#ae232d] font-bold text-[10px]">Action needed</span>
                                                <p className="text-[10px] text-[#999]">Generate Bio</p>
                                            </div>
                                        )}
                                        {u.linkedin && u.bio?.trim() && (
                                            <span className="text-emerald-600 text-[10px] font-bold flex items-center gap-0.5">
                                                <span className="material-symbols-outlined text-[12px]">check_circle</span>
                                                Complete
                                            </span>
                                        )}
                                    </div>

                                    {/* Edit */}
                                    <div className="pt-0.5 w-full lg:w-auto">
                                        <button onClick={() => setEditUser(u)}
                                            className="w-full lg:w-auto flex items-center justify-center lg:justify-start gap-1 px-3 py-2 lg:py-1.5 text-xs font-bold text-[#007a8a] border border-[#007a8a]/30 rounded-xl hover:bg-[#007a8a]/10 transition-colors whitespace-nowrap">
                                            <span className="material-symbols-outlined text-[14px]">edit</span>Edit
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {editUser && (
                <EditModal
                    user={editUser}
                    onClose={() => setEditUser(null)}
                    onSaved={updated => {
                        setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
                        setEditUser(null);
                    }}
                />
            )}

            {/* Create Presenter Modal */}
            {showCreatePresenter && (
                <CreatePresenterModal
                    onClose={() => setShowCreatePresenter(false)}
                    onCreated={() => { loadUsers(); setShowCreatePresenter(false); }}
                />
            )}
        </div>
    );
}
